(() => {
  const { createApp, ref, computed, onMounted } = Vue;

  const COLLECTION_HANDLE = "pralinen-konfigurator";
  const BOX_PRODUCT_HANDLE = "pralinenbox-konfigurator";

  const BOX_SIZES = [
    { key: "6er", rows: 2, cols: 3 },
    { key: "12er", rows: 3, cols: 4 },
    { key: "16er", rows: 4, cols: 4 },
    { key: "24er", rows: 4, cols: 6 },
    { key: "40er", rows: 5, cols: 8 },
  ];

  const style = document.createElement("style");
  style.textContent = `
    #pralinen-box {
      font-family: var(--font-body-family, Inter, sans-serif);
      background-color: #9da79d;
      padding: 2rem 0;
      box-sizing: border-box;
    }
    .bz-wrapper {
      display: grid;
      grid-template-columns: 160px 1fr 260px;
      gap: 2rem;
      align-items: start;
      width: 100%;
      max-width: 1100px;
      margin: 0 auto;
      min-height: 60vh;
    }
    @media (max-width: 900px) {
      .bz-wrapper { grid-template-columns: 1fr; }
    }

    .bz-slider {
      display: flex;
      flex-direction: column;
      overflow-y: auto;
      max-height: 70vh;
      position: relative;
      scroll-behavior: smooth;
      padding: 0.5rem;
      mask-image: linear-gradient(to bottom, transparent, black 10%, black 90%, transparent);
    }
    .bz-praline {
      aspect-ratio: 1/1;
      margin-bottom: .75rem;
      border-radius: .5rem;
      overflow: hidden;
      cursor: grab;
      transition: transform .15s ease;
      background: #fff;
      box-shadow: 0 1px 4px rgba(0,0,0,.08);
    }
    .bz-praline img {
      width: 100%; height: 100%; object-fit: cover;
    }
    .bz-praline:hover { transform: scale(1.03); }

    .bz-box-area {
      background: rgba(255,255,255,0.2);
      border-radius: 1rem;
      box-shadow: inset 0 0 8px rgba(0,0,0,.1);
      padding: 2rem;
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: center;
    }
    .bz-box-image {
      width: 100%;
      max-width: 320px;
      aspect-ratio: 1/1;
      object-fit: contain;
      margin-bottom: 1rem;
      border-radius: .6rem;
      background: #f0f0f0;
    }
    .bz-grid {
      display: grid;
      gap: .4rem;
      justify-content: center;
    }
    .bz-cell {
      aspect-ratio: 1/1;
      border: 1px solid rgba(255,255,255,.4);
      background: rgba(255,255,255,.25);
      border-radius: .4rem;
      position: relative;
      overflow: hidden;
      transition: background .2s ease, transform .1s ease;
    }
    .bz-cell.drop {
      background: rgba(245,134,52,.3);
      transform: scale(1.04);
    }
    .bz-cell img {
      width: 100%; height: 100%; object-fit: cover;
    }
    .bz-remove {
      position: absolute;
      top: .2rem; right: .2rem;
      background: rgba(255,255,255,0.8);
      border: none;
      border-radius: 50%;
      font-size: .8rem;
      cursor: pointer;
      line-height: 1;
      padding: .2rem .4rem;
    }

    .bz-controls {
      display: flex;
      flex-direction: column;
      gap: 1rem;
      background: rgba(255,255,255,0.85);
      border-radius: .75rem;
      padding: 1rem;
      box-shadow: 0 2px 8px rgba(0,0,0,.05);
    }
    .bz-controls label {
      font-weight: 500;
      color: #3b2a28;
    }
    .bz-controls select {
      width: 100%;
      padding: .5rem;
      border-radius: .4rem;
      border: 1px solid #c0bebe;
    }
    .bz-summary {
      font-weight: 600;
      font-size: 1.1rem;
      color: #3b2a28;
    }
    .bz-btn {
      appearance: none;
      border: none;
      border-radius: .5rem;
      padding: .7rem 1rem;
      font-weight: 600;
      cursor: pointer;
      background: #3b2a28;
      color: white;
      transition: background .2s ease;
    }
    .bz-btn:hover {
      background: #f58634;
    }
  `;
  document.head.appendChild(style);

  function money(amount, currency = "EUR") {
    try {
      return new Intl.NumberFormat("de-DE", {
        style: "currency",
        currency,
      }).format(amount);
    } catch {
      return amount.toFixed(2) + " " + currency;
    }
  }

  async function fetchCollection(handle) {
    const res = await fetch(`/collections/${handle}/products.json`);
    const json = await res.json();
    return json.products.map((p) => ({
      handle: p.handle,
      title: p.title,
      image: p.images?.[0]?.src || "",
      price: parseFloat(p.variants[0].price),
    }));
  }

  async function fetchBoxProduct(handle) {
    const res = await fetch(`/products/${handle}.js`);
    return await res.json();
  }

  createApp({
    setup() {
      const pralines = ref([]);
      const grid = ref([]);
      const activeSize = ref("12er");
      const rows = ref(3);
      const cols = ref(4);
      const total = computed(() =>
        grid.value.reduce((sum, item) => {
          const p = pralines.value.find((x) => x.handle === item);
          return sum + (p?.price || 0);
        }, 0)
      );
      const boxImage = ref(null);
      const loading = ref(true);

      const setSize = (key) => {
        const size = BOX_SIZES.find((x) => x.key === key);
        if (!size) return;
        activeSize.value = key;
        rows.value = size.rows;
        cols.value = size.cols;
        grid.value = Array(size.rows * size.cols).fill(null);
      };

      function onDragStart(e, handle) {
        e.dataTransfer.setData("text/plain", handle);
      }
      function onDragOver(e) {
        e.preventDefault();
        e.currentTarget.classList.add("drop");
      }
      function onDragLeave(e) {
        e.currentTarget.classList.remove("drop");
      }
      function onDrop(e, i) {
        e.preventDefault();
        e.currentTarget.classList.remove("drop");
        const h = e.dataTransfer.getData("text/plain");
        if (!h) return;
        const next = grid.value.slice();
        next[i] = h;
        grid.value = next;
      }
      function removeCell(i) {
        const next = grid.value.slice();
        next[i] = null;
        grid.value = next;
      }
      function addToCart() {
        alert(`Box (${activeSize.value}) mit ${grid.value.filter(Boolean).length} Pralinen hinzugefügt.`);
      }

      onMounted(async () => {
        const [prods, box] = await Promise.all([
          fetchCollection(COLLECTION_HANDLE),
          fetchBoxProduct(BOX_PRODUCT_HANDLE),
        ]);
        pralines.value = prods;
        boxImage.value = box.images[0] || null;
        setSize(activeSize.value);
        loading.value = false;
      });

      return {
        pralines,
        grid,
        rows,
        cols,
        total,
        activeSize,
        setSize,
        onDragStart,
        onDragOver,
        onDragLeave,
        onDrop,
        removeCell,
        addToCart,
        boxImage,
        loading,
        money,
      };
    },
    template: `
      <div class="bz-wrapper">
        <div class="bz-slider">
          <div v-for="p in pralines" :key="p.handle" class="bz-praline" draggable="true" @dragstart="(e)=>onDragStart(e,p.handle)">
            <img :src="p.image" :alt="p.title">
          </div>
        </div>
        <div class="bz-box-area">
          <img v-if="boxImage" :src="boxImage" alt="Box" class="bz-box-image" />
          <div class="bz-grid" :style="{ gridTemplateColumns: 'repeat(' + cols + ', 1fr)' }">
            <div v-for="(_,i) in rows*cols" :key="i" class="bz-cell" @dragover="onDragOver" @dragleave="onDragLeave" @drop="(e)=>onDrop(e,i)">
              <template v-if="grid[i]">
                <img :src="(pralines.find(p=>p.handle===grid[i])||{}).image">
                <button class="bz-remove" @click="removeCell(i)">×</button>
              </template>
            </div>
          </div>
        </div>
        <div class="bz-controls">
          <label>Boxgröße</label>
          <select v-model="activeSize" @change="setSize(activeSize)">
            <option v-for="s in BOX_SIZES.map(s=>s.key)" :value="s">{{ s }}</option>
          </select>
          <div class="bz-summary">Summe: {{ money(total) }}</div>
          <button class="bz-btn" @click="addToCart">In den Warenkorb</button>
        </div>
      </div>
    `,
  }).mount("#pralinen-box");
})();
